export const MODEL_PROVIDER_CONFIGS = [
  {
    id: "openai",
    label: "OpenAI",
    description: "GPT-class models for generation, critique, and utility tasks.",
    secretLabel: "OpenAI API key",
    placeholder: "sk-...",
    defaultModel: "gpt-4.1",
    supportsEndpoint: false,
  },
  {
    id: "anthropic",
    label: "Anthropic",
    description: "Claude models for long-context writing and reasoning.",
    secretLabel: "Anthropic API key",
    placeholder: "sk-ant-...",
    defaultModel: "claude-sonnet-4",
    supportsEndpoint: false,
  },
  {
    id: "gemini",
    label: "Gemini",
    description: "Google multimodal models for generation and analysis.",
    secretLabel: "Gemini API key",
    placeholder: "AIza...",
    defaultModel: "gemini-2.5-pro",
    supportsEndpoint: false,
  },
  {
    id: "deepseek",
    label: "DeepSeek",
    description: "Cost-efficient OpenAI-compatible models for generation and reasoning.",
    secretLabel: "DeepSeek API key",
    placeholder: "sk-...",
    defaultModel: "deepseek-chat",
    supportsEndpoint: false,
  },
  {
    id: "openrouter",
    label: "OpenRouter",
    description: "Route across multiple hosted model providers.",
    secretLabel: "OpenRouter API key",
    placeholder: "sk-or-...",
    defaultModel: "anthropic/claude-sonnet-4",
    supportsEndpoint: false,
  },
  {
    id: "ollama_local",
    label: "Ollama Local",
    description: "Use a local model runtime from this workspace.",
    secretLabel: "Optional API key",
    placeholder: "optional",
    defaultModel: "llama3.1",
    supportsEndpoint: true,
    endpointPlaceholder: "http://localhost:11434",
    local: true,
  },
  {
    id: "ollama_cloud",
    label: "Ollama Cloud",
    description: "Ollama's hosted models (BYOK) for generation. The same key also powers Web Research (Ollama Web Search).",
    secretLabel: "Ollama Cloud API key",
    placeholder: "ollama-...",
    defaultModel: "gpt-oss:120b",
    supportsEndpoint: true,
    endpointPlaceholder: "https://ollama.com/v1",
    // LLM provider (the default "model" group). Its key is independently picked
    // up by Web Research when "Via: Ollama Web Search" is selected.
  },
];

// Providers default to the "model" group unless tagged otherwise (e.g. research).
// Resolve from the static config by id too, so a stale localStorage cache (whose
// provider objects predate the `kind` field) still groups correctly.
export function providerKind(provider) {
  if (provider?.kind) return provider.kind;
  const cfg = MODEL_PROVIDER_CONFIGS.find((c) => c.id === provider?.id);
  return cfg?.kind || "model";
}

const DEFAULT_SETTINGS = {
  defaultProvider: "anthropic",
  // "sources_only" keeps every run grounded via one fast web-search call;
  // "deep" (full-page fetch of top results) is available per-run when wanted.
  defaultResearch: "sources_only",
  requireApproval: true,
  publishingGuard: true,
  autosave: true,
};

function nowIso() {
  return new Date().toISOString();
}

export function maskSecret(secret = "") {
  const clean = String(secret).trim();
  if (!clean) return "";
  if (clean.length <= 8) return "****";
  return `${clean.slice(0, 4)}...${clean.slice(-4)}`;
}

export function createInitialModelSettings(overrides = {}) {
  return {
    ...DEFAULT_SETTINGS,
    ...overrides,
    providers: MODEL_PROVIDER_CONFIGS.map((provider) => ({
      ...provider,
      configured: false,
      enabled: true,
      maskedKey: "",
      endpoint: "",
      status: "not_configured",
      lastTestedAt: null,
      lastError: "",
      defaultModel: provider.defaultModel,
    })),
  };
}

function updateProvider(settings, providerId, updater) {
  const base = settings?.providers?.length ? settings : createInitialModelSettings(settings || {});
  return {
    ...base,
    providers: base.providers.map((provider) => (
      provider.id === providerId ? updater(provider) : provider
    )),
  };
}

export function saveProviderCredential(settings, providerId, patch = {}) {
  return updateProvider(settings, providerId, (provider) => {
    const nextEndpoint = patch.endpoint !== undefined ? patch.endpoint.trim() : provider.endpoint;
    const maskedKey = patch.apiKey ? maskSecret(patch.apiKey) : provider.maskedKey;
    const configured = Boolean(maskedKey || nextEndpoint);

    return {
      ...provider,
      maskedKey,
      endpoint: nextEndpoint,
      defaultModel: patch.defaultModel?.trim() || provider.defaultModel,
      configured,
      status: configured ? "saved" : "not_configured",
      lastError: "",
      updatedAt: nowIso(),
    };
  });
}

export function testProviderConnection(settings, providerId) {
  return updateProvider(settings, providerId, (provider) => {
    const configured = Boolean(provider.maskedKey || provider.endpoint);
    if (!configured) {
      return {
        ...provider,
        configured: false,
        status: "error",
        lastError: "Add a key or endpoint before testing.",
        lastTestedAt: nowIso(),
      };
    }

    return {
      ...provider,
      configured: true,
      status: "connected",
      lastError: "",
      lastTestedAt: nowIso(),
    };
  });
}

// Map a backend ProviderCredentialRead onto the local provider shape.
export function applyBackendCredential(settings, cred) {
  if (!cred?.id) return settings;
  return updateProvider(settings, cred.id, (provider) => ({
    ...provider,
    maskedKey: cred.fingerprint || provider.maskedKey || "",
    endpoint: cred.endpoint ?? provider.endpoint,
    defaultModel: cred.default_model || provider.defaultModel,
    configured: Boolean(cred.configured),
    enabled: cred.enabled !== undefined ? Boolean(cred.enabled) : (provider.enabled !== false),
    status: cred.status || provider.status,
    lastError: cred.last_error || "",
    lastTestedAt: cred.last_tested_at || provider.lastTestedAt || null,
  }));
}

// Soft on/off — keep the key, flip enabled.
export function setProviderEnabled(settings, providerId, enabled) {
  return updateProvider(settings, providerId, (provider) => ({ ...provider, enabled: Boolean(enabled) }));
}

// Remove a saved key: reset the provider card back to an unconfigured state.
export function clearProviderCredential(settings, providerId) {
  return updateProvider(settings, providerId, (provider) => ({
    ...provider,
    maskedKey: "",
    endpoint: "",
    configured: false,
    enabled: true,
    status: "not_configured",
    lastError: "",
    lastTestedAt: null,
  }));
}

// Merge a list of backend credentials on hydration.
export function applyBackendProviderList(settings, creds = []) {
  const base = settings?.providers?.length ? settings : createInitialModelSettings(settings || {});
  return creds.reduce((acc, cred) => applyBackendCredential(acc, cred), base);
}

export function applyBackendModelDefaults(settings, defaults = {}) {
  const base = settings?.providers?.length ? settings : createInitialModelSettings(settings || {});
  return defaults.default_provider
    ? { ...base, defaultProvider: defaults.default_provider }
    : base;
}

// Apply a backend test result (connected / error + message).
export function applyProviderTestResult(settings, providerId, result = {}) {
  return updateProvider(settings, providerId, (provider) => ({
    ...provider,
    status: result.status || (result.ok ? "connected" : "error"),
    configured: result.ok ? true : Boolean(provider.maskedKey || provider.endpoint),
    lastError: result.ok ? "" : result.message || "Connection failed",
    lastTestedAt: result.last_tested_at || nowIso(),
  }));
}

export function updateModelDefaults(settings, patch = {}) {
  return {
    ...(settings?.providers ? settings : createInitialModelSettings(settings || {})),
    ...patch,
  };
}

// The starting model for a run: the user's default provider IF it's configured,
// otherwise the first connected/configured provider — never a provider with no
// key (which is why fresh DeepSeek-only users used to see "Anthropic").
export function resolveDefaultModel(settings) {
  const base = settings?.providers?.length ? settings : createInitialModelSettings(settings || {});
  const configured = base.providers.filter((p) => p.configured && p.enabled !== false);
  const provider =
    configured.find((p) => p.id === base.defaultProvider) ||
    configured.find((p) => p.status === "connected") ||
    configured[0] ||
    base.providers.find((p) => p.id === base.defaultProvider) ||
    base.providers[0];
  return { providerId: provider?.id || "", model: provider?.defaultModel || "" };
}

export function resolveRunModelSettings(settings, overrides = {}) {
  const base = settings?.providers?.length ? settings : createInitialModelSettings(settings || {});
  const requestedProviderId = overrides.providerId || overrides.model || base.defaultProvider || DEFAULT_SETTINGS.defaultProvider;
  const provider = base.providers.find((item) => item.id === requestedProviderId)
    || base.providers.find((item) => item.id === DEFAULT_SETTINGS.defaultProvider)
    || base.providers[0];
  const modelName = overrides.modelName?.trim() || overrides.defaultModel?.trim() || provider?.defaultModel || "";

  return {
    providerId: provider?.id || requestedProviderId,
    providerLabel: provider?.label || requestedProviderId,
    modelName,
    researchMode: overrides.researchMode || overrides.research_mode || base.defaultResearch || DEFAULT_SETTINGS.defaultResearch,
    configured: Boolean(provider?.configured),
    status: provider?.status || "not_configured",
    endpoint: provider?.endpoint || "",
  };
}
